import { useMutation, UseMutationOptions } from '@tanstack/react-query'
import { useToast } from 'common/toast/hook'
import { FetchError } from 'queries'
import { fetchWithAuth } from 'queries/fetch-with-auth'
import { postPortalSessionURL } from 'queries/routes'

export const POST_PORTAL_SESSION_ERROR = 'Couldn’t open subscription management — please try again.'

export interface PostPortalSessionVariables {
  subscriptionId: string
  token: string | null
}

export interface PortalSessionResponse {
  data: {
    url: string
  }
}

export async function postPortalSession({
  subscriptionId,
  token,
}: PostPortalSessionVariables): Promise<PortalSessionResponse> {
  const response = await fetchWithAuth(postPortalSessionURL(), token, {
    method: 'POST',
    body: JSON.stringify({ subscription_id: subscriptionId }),
  })

  if (!response.ok) {
    const { status, statusText } = response
    let cause: Error | undefined
    try {
      cause = (await response.json()) as Error
    } catch {
      cause = undefined
    }
    throw new FetchError(POST_PORTAL_SESSION_ERROR, { cause }, status, statusText)
  }

  return response.json()
}

export const usePostPortalSession = (
  options?: UseMutationOptions<PortalSessionResponse, FetchError, PostPortalSessionVariables>,
) => {
  const toast = useToast()

  return useMutation<PortalSessionResponse, FetchError, PostPortalSessionVariables>(postPortalSession, {
    ...options,
    onError: (error) => toast.error(error.message),
  })
}
